import { Check, XCircle, Package, Truck, CheckCircle2 } from 'lucide-react';
import { normalizeStatus } from '@/lib/orderStatus';

/**
 * Two-stage order progress tracker for the unified status model.
 *
 * Primary stage (always visible): Placed → Reviewed → Confirmed.
 * Secondary stage (revealed once status ≥ confirmed):
 *   Packing → Ready → Closed.
 *
 * Payment is decoupled from status (paymentStatus drives the "Paid" reflection).
 * Cancelled / rejected render their own banner.
 */
const PRIMARY = [
  { key: 'checkout_complete', label: 'Placed' },
  { key: 'under_review', label: 'Reviewed' },
  { key: 'confirmed', label: 'Confirmed' },
];

const SECONDARY = [
  { key: 'packing', label: 'Packing', Icon: Package },
  { key: 'ready_for_collection', label: 'Ready', Icon: Truck },
  { key: 'closed', label: 'Closed', Icon: CheckCircle2 },
];

const TrackerNode = ({ step, i, total, activeIdx, color = 'teal', compact, updatedAt }) => {
  const reached = i <= activeIdx;
  const isCurrent = i === activeIdx;
  const dotSize = compact ? 'h-3 w-3' : 'h-7 w-7 sm:h-8 sm:w-8';
  const isLast = i === total - 1;
  const baseRing = color === 'gold' ? 'bg-gold border-gold' : 'bg-teal border-teal';
  const idleRing = color === 'gold' ? 'border-gold/40' : 'border-gold/40';
  const ringHighlight = color === 'gold' ? 'ring-gold/25' : 'ring-teal/20';
  const trackColor = color === 'gold' ? 'bg-gold' : 'bg-teal';

  return (
    <div className={`flex items-center ${isLast ? '' : 'flex-1'} ${compact ? '' : 'min-w-0'}`}>
      <div className={`flex flex-col items-center ${compact ? '' : 'flex-shrink-0'}`}>
        <div
          data-testid={`tracker-node-${step.key}`}
          className={`${dotSize} rounded-full flex items-center justify-center transition-all duration-300 ${
            reached ? `${baseRing} border-2` : `bg-transparent border-2 ${idleRing}`
          } ${isCurrent && !compact ? `ring-4 ${ringHighlight}` : ''}`}
        >
          {reached && !compact && (
            step.Icon
              ? <step.Icon className="h-4 w-4 text-white" strokeWidth={2.5} />
              : <Check className="h-4 w-4 text-white" strokeWidth={3} />
          )}
        </div>
        {!compact && (
          <div className="mt-2 text-center">
            <p
              className={`font-body text-[10px] sm:text-xs uppercase tracking-wider ${
                reached ? 'text-foreground font-semibold' : 'text-foreground/40'
              }`}
            >
              {step.label}
            </p>
            {isCurrent && updatedAt && (
              <p className="font-body text-[10px] text-foreground/40 mt-0.5">
                {new Date(updatedAt).toLocaleDateString('en-ZA', {
                  month: 'short',
                  day: 'numeric',
                  timeZone: 'Africa/Johannesburg',
                })}
              </p>
            )}
          </div>
        )}
      </div>
      {!isLast && (
        <div
          className={`flex-1 h-[2px] mx-1 sm:mx-2 transition-all duration-300 ${
            i < activeIdx ? trackColor : 'bg-border'
          } ${compact ? 'mt-0' : '-mt-5 sm:-mt-6'}`}
        />
      )}
    </div>
  );
};


function primaryIndex(status) {
  switch (status) {
    case 'checkout_complete':
      return 0;
    case 'under_review':
      return 1;
    case 'confirmed':
    case 'packing':
    case 'ready_for_collection':
    case 'collection_notification_sent':
    case 'closed':
      return 2;
    default:
      return -1;
  }
}

function secondaryIndex(status) {
  switch (status) {
    case 'packing': return 0;
    case 'ready_for_collection': return 1;
    case 'collection_notification_sent': return 1;
    case 'closed': return 2;
    default: return -1;
  }
}

const showSecondary = (status) =>
  ['confirmed', 'packing', 'ready_for_collection', 'collection_notification_sent', 'closed'].includes(status);

export const OrderProgressTracker = ({ status: rawStatus, paymentStatus, variant = 'full', updatedAt }) => {
  const status = normalizeStatus(rawStatus);
  if (status === 'cancelled' || status === 'rejected') {
    return (
      <div
        data-testid="order-progress-cancelled"
        className="flex items-center gap-2 px-3 py-2 rounded-full bg-red-900/20 border border-red-700/40 w-fit"
      >
        <XCircle className="h-4 w-4 text-red-400" strokeWidth={2} />
        <span className="font-body text-xs font-semibold text-red-400 uppercase tracking-wider line-through">
          {status === 'rejected' ? 'Rejected' : 'Cancelled'}
        </span>
      </div>
    );
  }

  const compact = variant === 'compact';
  let primaryIdx = primaryIndex(status);
  // Payment is tracked separately from the fulfillment pipeline (admin can mark
  // an EFT/cash order as paid without advancing the stage). Reflect that here so
  // a paid order never looks "stuck" before the Paid node.
  if (paymentStatus === 'paid' && primaryIdx < 1) primaryIdx = 1;
  const secondaryIdx = secondaryIndex(status);
  const renderSecondary = showSecondary(status);

  return (
    <div className="space-y-5" data-testid={`order-progress-${variant}`}>
      <div className={`flex items-center w-full ${compact ? 'gap-1.5' : 'gap-2 sm:gap-3'}`}>
        {PRIMARY.map((step, i) => (
          <TrackerNode key={step.key} step={step} i={i} total={PRIMARY.length} activeIdx={primaryIdx} color="teal" compact={compact} updatedAt={updatedAt} />
        ))}
      </div>

      {renderSecondary && (
        <div
          data-testid="order-progress-fulfillment"
          className="pt-4 border-t border-gold/15"
        >
          <p className="font-body text-[10px] uppercase tracking-[0.2em] text-gold/80 font-semibold mb-3">
            Fulfillment
          </p>
          <div className={`flex items-center w-full ${compact ? 'gap-1.5' : 'gap-2 sm:gap-3'}`}>
            {SECONDARY.map((step, i) => (
              <TrackerNode key={step.key} step={step} i={i} total={SECONDARY.length} activeIdx={secondaryIdx} color="gold" compact={compact} updatedAt={updatedAt} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
